import { usePageMeta } from "../lib/seo";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Zap, Gauge, TreePine, Shuffle, Minus, Plus, Hotel, Home, Tent, Sparkles, Car, Search, Check, Gift, CheckCircle2, XCircle, X, Repeat, AlertTriangle, ChevronUp, ChevronDown, Bike, Mountain, Star, Warehouse } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API_BASE } from "../lib/api";
import { NETWORKS } from "../lib/chargerNetworks";
import { MapPhotoImport } from "../components/MapPhotoImport";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";

const STEPS = ["Trip & data", "Route", "Voorkeur", "Gezelschap", "Verblijf", "Voertuig", "Overzicht"];

const routePrefs = [
  { id: "autostrade", icon: Gauge, title: "Autostrade", km: 450, text: "Snel op bestemming, langere dagetappes (±450 km/dag)" },
  { id: "mix", icon: Shuffle, title: "Mix", km: 350, text: "Balans tussen snelheid en beleving (±350 km/dag)" },
  { id: "nationaal", icon: TreePine, title: "Nationale wegen", km: 260, text: "Rustig en scenic, kortere etappes (±260 km/dag)" },
  { id: "bochtig", icon: Mountain, title: "Bochtig & schilderachtig", km: 220, text: "Kronkelwegen en passen, geen autostrade (±220 km/dag) — ideaal voor de moto" },
];

const accoTypes = [
  { id: "hotel", icon: Hotel, label: "Hotel" },
  { id: "bnb", icon: Home, label: "B&B" },
  { id: "camping", icon: Tent, label: "Camping" },
  { id: "glamping", icon: Sparkles, label: "Glamping" },
  { id: "verras", icon: Gift, label: "Verras me" },
];

const LocFeedback = ({ status }) => {
  if (!status) return null;
  if (status.state === "checking") return <p className="text-xs text-muted-foreground animate-pulse mt-1">Plaats opzoeken...</p>;
  if (status.state === "ok") return <p className="text-xs text-primary flex items-center gap-1 mt-1"><CheckCircle2 className="w-3.5 h-3.5 shrink-0" /> Gevonden: {status.name} — klopt dit niet? Typ dan preciezer, bv. "Venetië, Italië"</p>;
  if (status.state === "error") return <p className="text-xs text-red-600 flex items-center gap-1 mt-1"><XCircle className="w-3.5 h-3.5 shrink-0" /> Niet gevonden — controleer de schrijfwijze</p>;
  return null;
};

const Counter = ({ label, value, onChange, min = 0, testId }) => (
  <div className="flex items-center justify-between bg-card rounded-2xl p-5">
    <span className="font-medium">{label}</span>
    <div className="flex items-center gap-4">
      <button data-testid={`${testId}-minus`} type="button" onClick={() => onChange(Math.max(min, value - 1))}
        className="w-10 h-10 rounded-full bg-secondary hover:bg-primary/15 flex items-center justify-center active:scale-90 transition-[transform,background-color] duration-150">
        <Minus className="w-4 h-4" />
      </button>
      <span data-testid={`${testId}-value`} className="font-mono font-semibold w-6 text-center">{value}</span>
      <button data-testid={`${testId}-plus`} type="button" onClick={() => onChange(value + 1)}
        className="w-10 h-10 rounded-full bg-secondary hover:bg-primary/15 flex items-center justify-center active:scale-90 transition-[transform,background-color] duration-150">
        <Plus className="w-4 h-4" />
      </button>
    </div>
  </div>
);

const VehicleThumb = ({ brand, model, vtype }) => {
  const [err, setErr] = useState(false);
  const Icon = vtype === "moto" ? Bike : Car;
  if (err) {
    return (
      <div className="w-20 h-14 rounded-lg bg-secondary flex items-center justify-center shrink-0">
        <Icon className="w-6 h-6 text-muted-foreground/60" />
      </div>
    );
  }
  return (
    <img src={`${API_BASE}/vehicle-image?brand=${encodeURIComponent(brand)}&model=${encodeURIComponent(model)}${vtype ? `&vtype=${encodeURIComponent(vtype)}` : ""}`}
      alt={`${brand} ${model}`} loading="lazy" onError={() => setErr(true)}
      className="w-20 h-14 rounded-lg object-cover bg-secondary shrink-0" />
  );
};

const MotoPicker = ({ motos, garage = [], value, onSelect, testId }) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const results = useMemo(() => motos.filter((m) => `${m.brand} ${m.model}`.toLowerCase().includes(q.toLowerCase())).slice(0, 8), [motos, q]);
  const garageResults = useMemo(
    () => garage.filter((g) => `${g.nickname || ""} ${(g.vehicle || {}).brand} ${(g.vehicle || {}).model}`.toLowerCase().includes(q.toLowerCase())).slice(0, 4),
    [garage, q]
  );
  return (
    <div className="relative flex-1 min-w-0">
      <Input data-testid={testId} value={open ? q : value ? `${value.brand} ${value.model}` : ""}
        onFocus={() => { setOpen(true); setQ(""); }} onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }} placeholder="Zoek moto..." className="h-11 rounded-xl" />
      {open && (results.length > 0 || garageResults.length > 0) && (
        <div className="absolute z-30 top-full mt-1 inset-x-0 bg-card rounded-xl shadow-xl border border-border max-h-56 overflow-y-auto">
          {garageResults.map((g) => (
            <button key={g.id} type="button" data-testid={`${testId}-garage-${g.id}`}
              className="w-full text-left px-3 py-2 text-sm hover:bg-secondary transition-colors flex items-center gap-1.5"
              onMouseDown={(e) => { e.preventDefault(); onSelect(g.vehicle); setOpen(false); }}>
              <Warehouse className="w-3.5 h-3.5 text-primary shrink-0" />
              <span className="truncate">{g.nickname || `${g.vehicle.brand} ${g.vehicle.model}`}{g.vehicle.rider_name ? ` (${g.vehicle.rider_name})` : ""}</span>
            </button>
          ))}
          {results.map((m) => (
            <button key={m.id} type="button" data-testid={`${testId}-option-${m.id}`}
              className="w-full text-left px-3 py-2 text-sm hover:bg-secondary transition-colors"
              onMouseDown={(e) => { e.preventDefault(); onSelect(m); setOpen(false); }}>
              {m.brand} {m.model} <span className="text-xs text-muted-foreground font-mono">±{m.range_km} km</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Wizard() {
  usePageMeta("Nieuwe trip — VoltRoute");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const [step, setStep] = useState(0);
  const [editLoading, setEditLoading] = useState(!!searchParams.get("edit"));
  const [vehicles, setVehicles] = useState([]);
  const [favIds, setFavIds] = useState([]);
  const [favOnly, setFavOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [manual, setManual] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fullReplan, setFullReplan] = useState(false);
  const [form, setForm] = useState({
    name: "", start_date: "", end_date: "",
    travel_mode: "ev",
    origin_street: "", origin_city: "", destination_street: "", destination_city: "",
    waypoints: [], round_trip: false, destination_is_home: false, stay_mode: "auto", return_stay_mode: "auto",
    return_end: "",
    destination_stay_name: "",
    departure_time: "09:00", start_battery_pct: 100,
    avoid_places: [], avoid_toll: false, avoid_ferry: false,
    planned_stays: [],
    planned_stays_return: [],
    suggest_alternatives: true,
    preferred_networks: [],
    charge_cards: [],
    max_km_per_day: "", max_hours_per_day: "", nights_to_destination: "", daily_budget: "",
    route_preference: "mix",
    travel_party: { adults: 2, children: 0, pets: 0 },
    accommodation_types: ["hotel"],
    vehicle: null,
    rider_name: "",
    extra_motos: [],
  });
  const [locStatus, setLocStatus] = useState({});
  const [checking, setChecking] = useState(false);
  const [manualVehicle, setManualVehicle] = useState({ brand: "", model: "", battery_kwh: "", range_km: "", consumption_kwh_100km: "", max_charge_kw: "", tank_liters: "", consumption_l_100km: "" });
  const [savedStays, setSavedStays] = useState([]);
  const [showReorderHint, setShowReorderHint] = useState(false);
  const dismissReorderHint = () => { setShowReorderHint(false); localStorage.setItem("vr_reorder_hint_seen", "1"); };

  const [motos, setMotos] = useState([]);
  useEffect(() => { api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/vehicles", { params: { vtype: "moto" } }).then((r) => setMotos(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/vehicles/favorites").then((r) => setFavIds(r.data.ids || [])).catch(() => {}); }, []);
  const [garage, setGarage] = useState([]);
  useEffect(() => { api.get("/garage").then((r) => setGarage(r.data.map((g) => ({ ...g, _v: Date.now() })))).catch(() => {}); }, []);
  useEffect(() => { api.get("/saved-places", { params: { kind: "stay" } }).then((r) => setSavedStays(r.data)).catch(() => {}); }, []);
  const [allCards, setAllCards] = useState([]);
  useEffect(() => {
    api.get("/me/charge-cards").then((r) => {
      setAllCards(r.data.all_cards || []);
      setForm((f) => ({ ...f, charge_cards: r.data.card_ids || [] }));
    }).catch(() => {});
  }, []);

  const CARD_NETWORKS = { ionity_passport: "ionity", fastned_gold: "fastned", tesla_app: "tesla", shell_recharge: "shell", enbw_basis: "enbw", enbw_m: "enbw", vattenfall: "vattenfall" };
  const toggleChargeCard = (id) => {
    const owned = form.charge_cards.includes(id);
    const nextCards = owned ? form.charge_cards.filter((c) => c !== id) : [...form.charge_cards, id];
    const nw = CARD_NETWORKS[id];
    let nets = form.preferred_networks;
    if (nw) {
      const stillNeeded = nextCards.some((c) => CARD_NETWORKS[c] === nw);
      if (!owned && !nets.includes(nw)) nets = [...nets, nw];
      if (owned && !stillNeeded) nets = nets.filter((n) => n !== nw);
    }
    set({ charge_cards: nextCards, preferred_networks: nets });
  };

  useEffect(() => {
    if (editId) return;
    const raw = sessionStorage.getItem("vr_wizard_prefill");
    if (!raw) return;
    sessionStorage.removeItem("vr_wizard_prefill");
    try {
      const p = JSON.parse(raw);
      setForm((f) => ({ ...f, ...p }));
      toast.success("Voorbeeld-trip ingevuld — kies alleen nog je reisdata!", { duration: 6000 });
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  useEffect(() => {
    if (!editId) return;
    api.get(`/trips/${editId}`).then(({ data: t }) => {
      setEditLoading(false);
      setForm((f) => ({
        ...f,
        name: t.name || "",
        start_date: t.start_date || "",
        end_date: t.end_date || "",
        origin_street: "", origin_city: t.origin_query || "",
        destination_street: "", destination_city: t.destination_query || "",
        waypoints: t.waypoints || [],
        round_trip: !!t.round_trip,
        return_end: t.return_end_query || "",
        travel_mode: t.travel_mode || "ev",
        destination_is_home: !!t.destination_is_home,
        stay_mode: (t.planned_stays || []).length ? "custom" : "auto",
        return_stay_mode: (t.planned_stays_return || []).length ? "custom" : "auto",
        planned_stays: (t.planned_stays || []).map((p) => ({ location: p.location, name: p.name || "", nights: p.nights || 1 })),
        planned_stays_return: (t.planned_stays_return || []).map((p) => ({ location: p.location, name: p.name || "", nights: p.nights || 1 })),
        suggest_alternatives: t.suggest_alternatives !== false,
        preferred_networks: t.preferred_networks || [],
        max_km_per_day: t.max_km_per_day ? String(t.max_km_per_day) : "",
        max_hours_per_day: t.max_hours_per_day ? String(t.max_hours_per_day) : "",
        nights_to_destination: t.nights_to_destination ? String(t.nights_to_destination) : "",
        daily_budget: t.daily_budget ? String(t.daily_budget) : "",
        route_preference: t.route_preference || "mix",
        travel_party: t.travel_party || { adults: 2, children: 0, pets: 0 },
        accommodation_types: t.accommodation_types?.length ? t.accommodation_types : ["hotel"],
        vehicle: t.vehicle || null,
        rider_name: t.vehicle?.rider_name || "",
        extra_motos: (t.extra_vehicles || []).map((v) => ({ rider_name: v.rider_name || "", vehicle: v })),
        destination_stay_name: t.destination_stay_name || "",
        avoid_places: t.avoid_places || [],
        avoid_toll: !!t.avoid_toll,
        avoid_ferry: !!t.avoid_ferry,
        departure_time: t.departure_time || "09:00",
        start_battery_pct: t.start_battery_pct || 100,
      }));
      if (localStorage.getItem("vr_reorder_hint_seen") !== "1") setShowReorderHint(true);
    }).catch(() => {
      toast.error("Trip kon niet geladen worden om aan te passen");
      navigate("/dashboard");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  const filteredVehicles = useMemo(() => {
    const base = (form.travel_mode === "moto" ? motos : vehicles).filter((v) => `${v.brand} ${v.model}`.toLowerCase().includes(search.toLowerCase()));
    const favs = new Set(favIds);
    const list = favOnly ? base.filter((v) => favs.has(v.id)) : base;
    return [...list].sort((a, b) => (favs.has(b.id) ? 1 : 0) - (favs.has(a.id) ? 1 : 0) || `${a.brand} ${a.model}`.localeCompare(`${b.brand} ${b.model}`));
  }, [vehicles, motos, form.travel_mode, search, favIds, favOnly]);

  const toggleFav = async (id) => {
    try {
      const { data } = await api.post(`/vehicles/favorites/${encodeURIComponent(id)}`);
      setFavIds(data.ids || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const applyMapImport = (p) => {
    const clean = Object.fromEntries(Object.entries(p || {}).filter(([, v]) => v !== "" && v !== null && v !== undefined));
    setForm((f) => ({ ...f, ...clean }));
  };
  const buildQuery = (street, city) => [street, city].map((s) => s.trim()).filter(Boolean).join(", ");
  const setWaypoint = (i, v) => { const w = [...form.waypoints]; w[i] = v; set({ waypoints: w }); };
  const removeWaypoint = (i) => set({ waypoints: form.waypoints.filter((_, j) => j !== i) });
  const setPlannedStay = (i, patch) => { const p = [...form.planned_stays]; p[i] = { ...p[i], ...patch }; set({ planned_stays: p }); };
  const removePlannedStay = (i) => set({ planned_stays: form.planned_stays.filter((_, j) => j !== i) });
  const setReturnStay = (i, patch) => { const p = [...form.planned_stays_return]; p[i] = { ...p[i], ...patch }; set({ planned_stays_return: p }); };
  const removeReturnStay = (i) => set({ planned_stays_return: form.planned_stays_return.filter((_, j) => j !== i) });
  const moveStay = (field, i, dir) => {
    if (showReorderHint) dismissReorderHint();
    const p = [...form[field]];
    const j = i + dir;
    if (j < 0 || j >= p.length) return;
    [p[i], p[j]] = [p[j], p[i]];
    set({ [field]: p });
  };
  const setAvoidPlace = (i, v) => { const a = [...form.avoid_places]; a[i] = v; set({ avoid_places: a }); };
  const removeAvoidPlace = (i) => set({ avoid_places: form.avoid_places.filter((_, j) => j !== i) });
  const addSavedStay = (s, target = "planned_stays") => {
    const stays = [...form[target]];
    const emptyIdx = stays.findIndex((p) => !p.location.trim());
    if (emptyIdx >= 0) stays[emptyIdx] = { ...stays[emptyIdx], location: s.location, name: stays[emptyIdx].name || s.name || "" };
    else stays.push({ location: s.location, name: s.name || "", nights: 1 });
    set({ [target]: stays });
  };

  useEffect(() => {
    const city = form.origin_city.trim();
    if (city.length < 3) return;
    const q = buildQuery(form.origin_street, form.origin_city);
    setLocStatus((s) => ({ ...s, origin: { state: "checking" } }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/geocode", { params: { q } });
        setLocStatus((s) => ({ ...s, origin: data.found ? { state: "ok", name: data.full_name } : { state: "error" } }));
      } catch {
        setLocStatus((s) => ({ ...s, origin: undefined }));
      }
    }, 800);
    return () => clearTimeout(t);
  }, [form.origin_street, form.origin_city]);

  useEffect(() => {
    const city = form.destination_city.trim();
    if (city.length < 3) return;
    const q = buildQuery(form.destination_street, form.destination_city);
    setLocStatus((s) => ({ ...s, destination: { state: "checking" } }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/geocode", { params: { q } });
        setLocStatus((s) => ({ ...s, destination: data.found ? { state: "ok", name: data.full_name } : { state: "error" } }));
      } catch {
        setLocStatus((s) => ({ ...s, destination: undefined }));
      }
    }, 800);
    return () => clearTimeout(t);
  }, [form.destination_street, form.destination_city]);

  const validateLocations = async () => {
    setChecking(true);
    const entries = [
      ["origin", buildQuery(form.origin_street, form.origin_city)],
      ...form.waypoints.map((w, i) => [`wp-${i}`, w.trim()]).filter(([, q]) => q),
      ...(form.stay_mode === "custom"
        ? form.planned_stays.map((p, i) => [`ps-${i}`, p.location.trim()]).filter(([, q]) => q)
        : []),
      ...(form.round_trip && form.return_stay_mode === "custom"
        ? form.planned_stays_return.map((p, i) => [`psr-${i}`, p.location.trim()]).filter(([, q]) => q)
        : []),
      ["destination", buildQuery(form.destination_street, form.destination_city)],
    ];
    const status = {};
    const failed = [];
    for (const [key, q] of entries) {
      try {
        const { data } = await api.get("/geocode", { params: { q } });
        status[key] = data.found ? { state: "ok", name: data.full_name } : { state: "error" };
        if (!data.found) failed.push({ key, label: q });
      } catch {
        status[key] = { state: "error" };
        failed.push({ key, label: q });
      }
    }
    setLocStatus(status);
    setChecking(false);
    return failed;
  };

  const [flashField, setFlashField] = useState(null);
  const flashTo = (fid) => {
    setFlashField(fid);
    setTimeout(() => document.getElementById(fid)?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
    setTimeout(() => setFlashField(null), 2800);
  };
  const flash = (fid) => (flashField === fid ? " ring-2 ring-red-500 ring-offset-2 animate-pulse" : "");
  const idFor = (key) => {
    if (key === "origin") return "wiz-origin";
    if (key === "destination") return "wiz-destination";
    const [kind, idx] = key.split("-");
    return kind === "wp" ? `wiz-wp-${idx}` : kind === "ps" ? `wiz-ps-${idx}` : `wiz-psr-${idx}`;
  };

  const blockReason = () => {
    switch (step) {
      case 0:
        if (!form.name) return { reason: "Geef je trip eerst een naam", field: "wiz-name" };
        if (!form.start_date || !form.end_date) return { reason: "Kies een vertrek- en einddatum", field: "wiz-dates" };
        if (form.end_date < form.start_date) return { reason: "De einddatum ligt vóór de vertrekdatum", field: "wiz-dates" };
        return null;
      case 1: {
        if (!form.origin_city.trim()) return { reason: "Vul je vertrekpunt in", field: "wiz-origin" };
        if (!form.destination_city.trim()) return { reason: "Vul je bestemming in", field: "wiz-destination" };
        if (form.stay_mode === "custom") {
          if (!form.planned_stays.length) return { reason: "Voeg minstens één heenreis-overnachting toe, of kies voor automatisch plannen", field: null };
          const i = form.planned_stays.findIndex((p) => !p.location.trim());
          if (i >= 0) return { reason: `Heenreis-overnachting ${i + 1} heeft nog geen locatie — vul een plaats in of verwijder de kaart met de X`, field: `wiz-ps-${i}` };
        }
        if (form.round_trip && form.return_stay_mode === "custom") {
          const i = form.planned_stays_return.findIndex((p) => !p.location.trim());
          if (i >= 0) return { reason: `Terugreis-overnachting ${i + 1} heeft nog geen locatie — vul een plaats in of verwijder de kaart met de X`, field: `wiz-psr-${i}` };
        }
        return null;
      }
      case 2: return form.route_preference ? null : { reason: "Kies eerst een routevoorkeur", field: null };
      case 3: return form.travel_party.adults >= 1 ? null : { reason: "Er is minstens 1 volwassene nodig", field: null };
      case 4: {
        if (form.stay_mode === "custom" && !form.suggest_alternatives) return null;
        return form.accommodation_types.length > 0 ? null : { reason: "Kies minstens één type verblijf", field: null };
      }
      case 5:
        if (form.travel_mode === "moto") {
          if (form.extra_motos.some((em) => !em.vehicle)) return { reason: "Kies een moto voor elke rijder — of verwijder de lege rij met de X", field: null };
          if (manual) return (manualVehicle.brand && manualVehicle.model && manualVehicle.range_km) ? null : { reason: "Vul merk, model en bereik per tank van je moto in", field: null };
          return form.vehicle ? null : { reason: "Kies eerst je moto (of vul hem handmatig in)", field: null };
        }
        if (manual) return (manualVehicle.brand && manualVehicle.model && manualVehicle.battery_kwh && manualVehicle.range_km) ? null : { reason: "Vul merk, model, batterijcapaciteit en rijbereik van je EV in", field: null };
        return form.vehicle ? null : { reason: "Kies eerst je EV (of vul hem handmatig in)", field: null };
      default: return null;
    }
  };

  const canNext = () => !blockReason();

  const next = async () => {
    const blocked = blockReason();
    if (blocked) {
      toast.error(blocked.reason);
      if (blocked.field) flashTo(blocked.field);
      return;
    }
    if (step === 1) {
      const failed = await validateLocations();
      if (failed.length) {
        toast.error(`Niet gevonden: ${failed.map((f) => f.label).join(", ")}. Controleer de spelling of maak de plaats specifieker (bv. "plaats, land").`);
        flashTo(idFor(failed[0].key));
        return;
      }
    }
    if (step === 5 && manual) {
      set({
        vehicle: form.travel_mode === "moto" ? {
          brand: manualVehicle.brand, model: manualVehicle.model,
          range_km: parseFloat(manualVehicle.range_km),
          tank_liters: parseFloat(manualVehicle.tank_liters) || null,
          consumption_l_100km: parseFloat(manualVehicle.consumption_l_100km) || 5.5,
          battery_kwh: 0, consumption_kwh_100km: 0, max_charge_kw: 0, type: "moto",
        } : {
          brand: manualVehicle.brand, model: manualVehicle.model,
          battery_kwh: parseFloat(manualVehicle.battery_kwh), range_km: parseFloat(manualVehicle.range_km),
          consumption_kwh_100km: parseFloat(manualVehicle.consumption_kwh_100km) || 16,
          max_charge_kw: parseFloat(manualVehicle.max_charge_kw) || 100,
        },
      });
      if (form.travel_mode === "moto" && manualVehicle.brand && manualVehicle.model) {
        api.post("/vehicles/moto-thanks", { brand: manualVehicle.brand, model: manualVehicle.model })
          .then((r) => toast.success(r.data.message, { duration: 12000 })).catch(() => {});
      }
    }
    setStep((s) => s + 1);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        start_date: form.start_date,
        end_date: form.end_date,
        origin_query: buildQuery(form.origin_street, form.origin_city),
        destination_query: buildQuery(form.destination_street, form.destination_city),
        waypoints: form.waypoints.map((w) => w.trim()).filter(Boolean),
        planned_stays: form.stay_mode === "custom"
          ? form.planned_stays.filter((p) => p.location.trim()).map((p) => ({ location: p.location.trim(), name: p.name.trim() || null, nights: parseInt(p.nights) || 1 }))
          : [],
        planned_stays_return: form.round_trip && form.return_stay_mode === "custom"
          ? form.planned_stays_return.filter((p) => p.location.trim()).map((p) => ({ location: p.location.trim(), name: p.name.trim() || null, nights: parseInt(p.nights) || 1 }))
          : [],
        suggest_alternatives: form.stay_mode === "custom" ? form.suggest_alternatives : false,
        preferred_networks: form.preferred_networks,
        round_trip: form.round_trip,
        return_end_query: form.round_trip && form.return_end.trim() ? form.return_end.trim() : null,
        travel_mode: form.travel_mode,
        destination_is_home: !form.round_trip && !!form.destination_is_home,
        max_km_per_day: form.stay_mode === "auto" && form.max_km_per_day ? parseFloat(form.max_km_per_day) : null,
        max_hours_per_day: form.stay_mode === "auto" && form.max_hours_per_day ? parseFloat(form.max_hours_per_day) : null,
        nights_to_destination: form.stay_mode === "auto" && form.nights_to_destination ? parseInt(form.nights_to_destination) : null,
        route_preference: form.route_preference,
        travel_party: form.travel_party,
        accommodation_types: form.accommodation_types,
        daily_budget: form.daily_budget ? parseFloat(form.daily_budget) : null,
        vehicle: form.travel_mode === "moto" ? { ...form.vehicle, rider_name: form.rider_name.trim() || null } : form.vehicle,
        extra_vehicles: form.travel_mode === "moto"
          ? form.extra_motos.filter((em) => em.vehicle).map((em) => ({ ...em.vehicle, rider_name: em.rider_name.trim() || null }))
          : [],
        destination_stay_name: form.destination_stay_name.trim() || null,
        avoid_places: form.avoid_places.map((a) => a.trim()).filter(Boolean),
        avoid_toll: form.avoid_toll,
        avoid_ferry: form.avoid_ferry,
        departure_time: form.departure_time || "09:00",
        start_battery_pct: form.start_battery_pct || 100,
        full_replan: editId ? fullReplan : false,
      };
      if (editId) {
        await api.put(`/trips/${editId}`, payload);
        toast.success("Trip wordt opnieuw berekend met je aanpassingen!");
        navigate(`/trip/${editId}`);
      } else {
        const { data } = await api.post("/trips", payload);
        toast.success("Trip wordt gegenereerd!");
        navigate(`/trip/${data.id}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  const toggleAcco = (id) => {
    const cur = form.accommodation_types;
    set({ accommodation_types: cur.includes(id) ? cur.filter((a) => a !== id) : [...cur, id] });
  };

  if (editLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-3" data-testid="wizard-edit-loading">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        <p className="text-sm text-muted-foreground">Trip laden…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 glass-panel border-b border-border/60">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center justify-between">
          <button data-testid="wizard-back-to-dashboard" onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-[color] duration-200">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </button>
          <div className="flex items-center gap-1.5">
            {STEPS.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${i === step ? "w-8 bg-primary" : i < step ? "w-4 bg-primary/50" : "w-4 bg-border"}`} />
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-12">
        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-accent mb-2">Stap {step + 1} van {STEPS.length} — {STEPS[step]}</p>
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.3 }}>
            {step === 0 && (
              <div className="space-y-6">
                <h1 className="font-heading font-extrabold text-3xl tracking-tight">Hoe heet je trip en wanneer vertrek je?</h1>
                <div className="grid grid-cols-2 gap-4" data-testid="wizard-travel-mode">
                  {[{ id: "ev", Icon: Car, title: "Elektrische auto", text: "Slimme laadstops op maat van jouw EV" }, { id: "moto", Icon: Bike, title: "Moto", text: "Bochtige routes, tankstops en moto-paklijst" }].map((m) => {
                    const active = form.travel_mode === m.id;
                    return (
                      <button key={m.id} data-testid={`wizard-mode-${m.id}`} type="button"
                        onClick={() => { if (form.travel_mode !== m.id) set({ travel_mode: m.id, vehicle: null, route_preference: m.id === "moto" ? "bochtig" : "mix" }); }}
                        className={`text-left rounded-2xl p-5 border-2 transition-[border-color,background-color] duration-200 ${active ? "border-primary bg-primary/5" : "border-transparent bg-card hover:bg-secondary/60"}`}>
                        <span className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${active ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                          <m.Icon className="w-5 h-5" />
                        </span>
                        <span className="font-heading font-bold block">{m.title}</span>
                        <span className="text-xs text-muted-foreground">{m.text}</span>
                      </button>
                    );
                  })}
                </div>
                {!editId && (
                  <div data-testid="wizard-map-import-banner" className="bg-primary/5 border border-primary/15 rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <p className="text-sm font-bold">📷 Heb je al een kaart of reisschema?</p>
                      <p className="text-xs text-muted-foreground">Upload een foto van een routekaart of dagschema, óf plak de tekst/tabel rechtstreeks ("Dag 1: Wenen...") — AI vult vertrek, bestemming, stops, datums en verblijfsnamen vooraf in.</p>
                    </div>
                    <MapPhotoImport onResult={applyMapImport} />
                  </div>
                )}
                <div id="wiz-name" className={`space-y-2 rounded-xl transition-shadow duration-300${flash("wiz-name")}`}>
                  <Label>Naam van je roadtrip</Label>
                  <Input data-testid="wizard-trip-name-input" value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="bv. Zomertrip Zuid-Frankrijk" className="h-12 rounded-xl" />
                </div>
                <div id="wiz-dates" className={`grid grid-cols-2 gap-4 rounded-xl transition-shadow duration-300${flash("wiz-dates")}`}>
                  <div className="space-y-2">
                    <Label>Vertrekdatum</Label>
                    <Input data-testid="wizard-start-date-input" type="date" value={form.start_date} onChange={(e) => set({ start_date: e.target.value })} className="h-12 rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label>Einddatum</Label>
                    <Input data-testid="wizard-end-date-input" type="date" value={form.end_date} min={form.start_date}
                      onChange={(e) => set({ end_date: e.target.value })}
                      className={`h-12 rounded-xl ${form.start_date && form.end_date && form.end_date < form.start_date ? "border-red-500 focus-visible:ring-red-500" : ""}`} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Vertrekuur (per dag aanpasbaar in je planning)</Label>
                    <Input data-testid="wizard-departure-time-input" type="time" value={form.departure_time}
                      onChange={(e) => set({ departure_time: e.target.value })} className="h-12 rounded-xl" />
                  </div>
                  {form.travel_mode !== "moto" && (
                  <div className="space-y-2">
                    <Label>Batterij bij vertrek: <span className="font-mono font-bold text-primary">{form.start_battery_pct}%</span></Label>
                    <input data-testid="wizard-battery-input" type="range" min="10" max="100" step="5" value={form.start_battery_pct}
                      onChange={(e) => set({ start_battery_pct: parseInt(e.target.value) })}
                      className="w-full h-12 accent-[#2D6A4F] cursor-pointer" />
                  </div>
                  )}
                </div>
                {form.start_date && form.end_date && form.end_date < form.start_date && (
                  <div data-testid="wizard-date-error" className="flex items-start gap-2 bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 rounded-xl px-4 py-3 text-sm font-medium">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    De einddatum ({form.end_date.split("-").reverse().join("/")}) ligt vóór de vertrekdatum ({form.start_date.split("-").reverse().join("/")}). Pas één van beide aan om verder te gaan.
                  </div>
                )}
              </div>
            )}

            {step === 1 && (
              <div className="space-y-6">
                <h1 className="font-heading font-extrabold text-3xl tracking-tight">Van waar naar waar?</h1>
                <div id="wiz-origin" className={`bg-card rounded-2xl p-5 space-y-3 transition-shadow duration-300${flash("wiz-origin")}`}>
                  <p className="text-xs tracking-[0.15em] uppercase font-semibold text-primary">Vertrekpunt</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input data-testid="wizard-origin-street-input" value={form.origin_street} onChange={(e) => set({ origin_street: e.target.value })} placeholder="Straat + nr (optioneel)" className="h-12 rounded-xl" />
                    <Input data-testid="wizard-origin-input" value={form.origin_city} onChange={(e) => set({ origin_city: e.target.value })} placeholder="Plaats, land — bv. Puurs, België" className="h-12 rounded-xl" />
                  </div>
                  <LocFeedback status={locStatus.origin} />
                </div>

                <div className="flex gap-2">
                  <button data-testid="wizard-stay-mode-auto" type="button" onClick={() => set({ stay_mode: "auto" })}
                    className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-[background-color,color] duration-200 ${form.stay_mode === "auto" ? "bg-primary text-primary-foreground" : "bg-card hover:bg-secondary"}`}>
                    Plan mijn overnachtingen automatisch
                  </button>
                  <button data-testid="wizard-stay-mode-custom" type="button" onClick={() => set({ stay_mode: "custom", planned_stays: form.planned_stays.length ? form.planned_stays : [{ location: "", name: "", nights: 1 }] })}
                    className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-[background-color,color] duration-200 ${form.stay_mode === "custom" ? "bg-primary text-primary-foreground" : "bg-card hover:bg-secondary"}`}>
                    Ik geef mijn overnachtingen zelf op
                  </button>
                </div>

                {form.waypoints.map((wp, i) => (
                  <div key={i} id={`wiz-wp-${i}`} className={`bg-card rounded-2xl p-5 space-y-3 transition-shadow duration-300${flash(`wiz-wp-${i}`)}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-xs tracking-[0.15em] uppercase font-semibold text-accent">Passeer zeker langs — via {i + 1}{form.stay_mode === "custom" ? " · zonder overnachting" : ""}</p>
                      <div className="relative flex items-center gap-1">
                        {showReorderHint && step === 1 && i === 0 && (
                          <div data-testid="reorder-hint" className="absolute -top-11 right-0 z-20 flex items-center gap-2 bg-primary text-primary-foreground text-xs font-semibold rounded-full pl-3.5 pr-2 py-2 shadow-lg whitespace-nowrap animate-bounce">
                            Nieuw: schuif je stops!
                            <button type="button" data-testid="reorder-hint-close" onClick={dismissReorderHint} className="p-0.5 rounded-full hover:bg-white/20"><X className="w-3 h-3" /></button>
                          </div>
                        )}
                        <button data-testid={`wizard-waypoint-up-${i}`} type="button" onClick={() => moveStay("waypoints", i, -1)} disabled={i === 0} title="Schuif omhoog"
                          className="p-1.5 rounded-full hover:bg-accent/10 text-accent disabled:opacity-25 disabled:cursor-default transition-[background-color] duration-200">
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button data-testid={`wizard-waypoint-down-${i}`} type="button" onClick={() => moveStay("waypoints", i, 1)} disabled={i === form.waypoints.length - 1} title="Schuif omlaag"
                          className="p-1.5 rounded-full hover:bg-accent/10 text-accent disabled:opacity-25 disabled:cursor-default transition-[background-color] duration-200">
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        <button data-testid={`wizard-remove-waypoint-${i}`} type="button" onClick={() => removeWaypoint(i)} className="p-1.5 rounded-full hover:bg-red-50 text-red-500 transition-[background-color] duration-200">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <Input data-testid={`wizard-waypoint-input-${i}`} value={wp} onChange={(e) => setWaypoint(i, e.target.value)} placeholder="Plaats, land — bv. Dijon, Frankrijk" className="h-12 rounded-xl" />
                    <LocFeedback status={locStatus[`wp-${i}`]} />
                  </div>
                ))}
                <button data-testid="wizard-add-waypoint-button" type="button" onClick={() => set({ waypoints: [...form.waypoints, ""] })}
                  className="w-full rounded-2xl border-2 border-dashed border-primary/40 hover:border-primary text-primary font-semibold text-sm py-3 flex items-center justify-center gap-2 transition-[border-color] duration-200">
                  <Plus className="w-4 h-4" /> Passeer zeker langs — via-punt toevoegen{form.stay_mode === "custom" ? " (zonder overnachting)" : ""}
                </button>

                {form.stay_mode === "custom" && (
                  <>
                    {savedStays.length > 0 && (
                      <div className="bg-card rounded-2xl p-4" data-testid="saved-stays-box">
                        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#3A6EA5] mb-2">Eerder gebruikte overnachtingen — klik om toe te voegen</p>
                        <div className="flex flex-wrap gap-2">
                          {savedStays.map((s) => (
                            <button key={s.id} data-testid={`saved-stay-chip-${s.id}`} type="button" onClick={() => addSavedStay(s)}
                              className="rounded-full bg-[#3A6EA5]/10 hover:bg-[#3A6EA5]/20 text-[#3A6EA5] text-xs font-semibold px-3.5 py-2 active:scale-95 transition-[transform,background-color] duration-200">
                              + {s.location}{s.name ? ` · ${s.name}` : ""}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {form.planned_stays.map((ps, i) => (
                      <div key={i} id={`wiz-ps-${i}`} className={`bg-card rounded-2xl p-5 space-y-3 transition-shadow duration-300${flash(`wiz-ps-${i}`)}`}>
                        <div className="flex items-center justify-between">
                          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#3A6EA5]">{form.round_trip ? "Heenreis — overnachting" : "Overnachting"} {i + 1}</p>
                          <div className="relative flex items-center gap-1">
                            {showReorderHint && step === 1 && i === 0 && (
                              <div data-testid="reorder-hint-stay" className="absolute -top-11 right-0 z-20 flex items-center gap-2 bg-primary text-primary-foreground text-xs font-semibold rounded-full pl-3.5 pr-2 py-2 shadow-lg whitespace-nowrap animate-bounce">
                                Nieuw: schuif je stops!
                                <button type="button" data-testid="reorder-hint-stay-close" onClick={dismissReorderHint} className="p-0.5 rounded-full hover:bg-white/20"><X className="w-3 h-3" /></button>
                              </div>
                            )}
                            <button data-testid={`wizard-stay-up-${i}`} type="button" onClick={() => moveStay("planned_stays", i, -1)} disabled={i === 0} title="Schuif omhoog"
                              className="p-1.5 rounded-full hover:bg-[#3A6EA5]/10 text-[#3A6EA5] disabled:opacity-25 disabled:cursor-default transition-[background-color] duration-200">
                              <ChevronUp className="w-4 h-4" />
                            </button>
                            <button data-testid={`wizard-stay-down-${i}`} type="button" onClick={() => moveStay("planned_stays", i, 1)} disabled={i === form.planned_stays.length - 1} title="Schuif omlaag"
                              className="p-1.5 rounded-full hover:bg-[#3A6EA5]/10 text-[#3A6EA5] disabled:opacity-25 disabled:cursor-default transition-[background-color] duration-200">
                              <ChevronDown className="w-4 h-4" />
                            </button>
                            <button data-testid={`wizard-remove-stay-${i}`} type="button" onClick={() => removePlannedStay(i)} className="p-1.5 rounded-full hover:bg-red-50 text-red-500 transition-[background-color] duration-200">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <Input data-testid={`wizard-stay-location-${i}`} value={ps.location} onChange={(e) => setPlannedStay(i, { location: e.target.value })} placeholder="Plaats, land — bv. Dijon, Frankrijk" className="h-12 rounded-xl" />
                          <Input data-testid={`wizard-stay-name-${i}`} value={ps.name} onChange={(e) => setPlannedStay(i, { name: e.target.value })} placeholder="Hotel/verblijf (optioneel)" className="h-12 rounded-xl" />
                        </div>
                        <div className="flex items-center gap-3">
                          <Label className="text-sm text-muted-foreground">Nachten:</Label>
                          <button data-testid={`wizard-stay-nights-minus-${i}`} type="button" onClick={() => setPlannedStay(i, { nights: Math.max(1, (parseInt(ps.nights) || 1) - 1) })}
                            className="w-8 h-8 rounded-full bg-secondary hover:bg-primary/15 flex items-center justify-center active:scale-90 transition-[transform,background-color] duration-150"><Minus className="w-3.5 h-3.5" /></button>
                          <span data-testid={`wizard-stay-nights-${i}`} className="font-mono font-semibold w-5 text-center">{ps.nights}</span>
                          <button data-testid={`wizard-stay-nights-plus-${i}`} type="button" onClick={() => setPlannedStay(i, { nights: (parseInt(ps.nights) || 1) + 1 })}
                            className="w-8 h-8 rounded-full bg-secondary hover:bg-primary/15 flex items-center justify-center active:scale-90 transition-[transform,background-color] duration-150"><Plus className="w-3.5 h-3.5" /></button>
                        </div>
                        <LocFeedback status={locStatus[`ps-${i}`]} />
                      </div>
                    ))}
                    <button data-testid="wizard-add-stay-button" type="button" onClick={() => set({ planned_stays: [...form.planned_stays, { location: "", name: "", nights: 1 }] })}
                      className="w-full rounded-2xl border-2 border-dashed border-[#3A6EA5]/40 hover:border-[#3A6EA5] text-[#3A6EA5] font-semibold text-sm py-3 flex items-center justify-center gap-2 transition-[border-color] duration-200">
                      <Plus className="w-4 h-4" /> Overnachting toevoegen
                    </button>
                    <p className="text-sm text-muted-foreground">De route en laadstops worden gepland tussen je overnachtingen, in de opgegeven volgorde.</p>
                  </>
                )}

                <div id="wiz-destination" className={`bg-card rounded-2xl p-5 space-y-3 transition-shadow duration-300${flash("wiz-destination")}`}>
                  <p className="text-xs tracking-[0.15em] uppercase font-semibold text-primary">Bestemming</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input data-testid="wizard-destination-street-input" value={form.destination_street} onChange={(e) => set({ destination_street: e.target.value })} placeholder="Straat + nr (optioneel)" className="h-12 rounded-xl" />
                    <Input data-testid="wizard-destination-input" value={form.destination_city} onChange={(e) => set({ destination_city: e.target.value })} placeholder="Plaats, land — bv. Nice, Frankrijk" className="h-12 rounded-xl" />
                  </div>
                  <LocFeedback status={locStatus.destination} />
                  {!form.round_trip && (
                    <div className="flex items-center justify-between gap-3 rounded-xl bg-secondary/60 px-4 py-3" data-testid="wizard-destination-home-row">
                      <div className="flex items-center gap-2.5">
                        <Home className="w-4 h-4 text-primary shrink-0" />
                        <div>
                          <p className="text-sm font-semibold">Eindbestemming is thuis</p>
                          <p className="text-xs text-muted-foreground">Geen overnachting nodig op het eindpunt — de trip eindigt bij aankomst</p>
                        </div>
                      </div>
                      <Switch data-testid="wizard-destination-home-switch" checked={form.destination_is_home} onCheckedChange={(v) => set(v ? { destination_is_home: true, destination_stay_name: "" } : { destination_is_home: false })} />
                    </div>
                  )}
                  {!form.destination_is_home && (
                    <>
                      <Input data-testid="wizard-destination-stay-input" value={form.destination_stay_name} onChange={(e) => set({ destination_stay_name: e.target.value })}
                        placeholder="Jouw verblijf op de eindbestemming (optioneel) — bv. Villa Les Oliviers" className="h-12 rounded-xl" />
                      <p className="text-xs text-muted-foreground">Je eindbestemming is ook een slaapplaats — vul je eigen verblijf in, of laat leeg en AI stelt er een voor.</p>
                    </>
                  )}
                </div>

                <div className="bg-card rounded-2xl p-5 space-y-3" data-testid="wizard-avoid-card">
                  <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#E07A5F]">Vermijd op de route</p>
                  {form.avoid_places.map((av, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input data-testid={`wizard-avoid-input-${i}`} value={av} onChange={(e) => setAvoidPlace(i, e.target.value)} placeholder="Plaats om te vermijden — bv. Paris" className="h-12 rounded-xl flex-1" />
                      <button data-testid={`wizard-remove-avoid-${i}`} type="button" onClick={() => removeAvoidPlace(i)} className="p-1.5 rounded-full hover:bg-red-50 text-red-500 transition-[background-color] duration-200">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button data-testid="wizard-add-avoid-button" type="button" onClick={() => set({ avoid_places: [...form.avoid_places, ""] })}
                    className="text-sm font-semibold text-[#E07A5F] hover:text-[#c96148] flex items-center gap-1.5 transition-[color] duration-200">
                    <Plus className="w-4 h-4" /> Plaats vermijden (bv. drukke stad)
                  </button>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <button data-testid="wizard-avoid-toll" type="button" onClick={() => set({ avoid_toll: !form.avoid_toll })}
                      className={`px-4 py-2 rounded-full text-sm font-semibold transition-[background-color,color] duration-200 ${form.avoid_toll ? "bg-[#E07A5F] text-white" : "bg-secondary hover:bg-[#E07A5F]/15"}`}>
                      Vermijd tolwegen
                    </button>
                    <button data-testid="wizard-avoid-ferry" type="button" onClick={() => set({ avoid_ferry: !form.avoid_ferry })}
                      className={`px-4 py-2 rounded-full text-sm font-semibold transition-[background-color,color] duration-200 ${form.avoid_ferry ? "bg-[#E07A5F] text-white" : "bg-secondary hover:bg-[#E07A5F]/15"}`}>
                      Vermijd veerboten
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">We leggen de route om deze plaatsen heen. Lukt dat niet volledig, dan zie je een melding bij je plan.</p>
                </div>


                <div className="bg-card rounded-2xl p-5 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <Repeat className="w-5 h-5 text-primary" />
                    </span>
                    <div>
                      <p className="font-heading font-bold text-sm">Heen en terug</p>
                      <p className="text-xs text-muted-foreground">Plan ook de terugrit naar je vertrekpunt, met verblijfdagen op je bestemming</p>
                    </div>
                  </div>
                  <Switch data-testid="wizard-round-trip-switch" checked={form.round_trip} onCheckedChange={(v) => set(v ? { round_trip: true, destination_is_home: false } : { round_trip: false })} />
                </div>

                {form.round_trip && (
                  <div className="bg-card rounded-2xl p-5 space-y-2" data-testid="wizard-return-end-card">
                    <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#B5838D]">Eindpunt van de terugreis (optioneel)</p>
                    <p className="text-xs text-muted-foreground">Laat leeg om terug te keren naar je vertrekpunt — of vul een andere plaats in waar de reis eindigt (bv. bij familie of een tweede verblijf).</p>
                    <p className="text-xs text-muted-foreground" data-testid="wizard-return-end-hint">Tip: je <strong>bestemming</strong> hierboven is het verste punt van je reis (het keerpunt). Vul je die toch gelijk aan dit eindpunt in, dan gebruikt de planner automatisch je laatste heenreis-overnachting als keerpunt.</p>
                    <Input data-testid="wizard-return-end-input" value={form.return_end} onChange={(e) => set({ return_end: e.target.value })}
                      placeholder='bv. "Antwerpen, België" — leeg = terug naar vertrekpunt' className="rounded-xl" />
                  </div>
                )}

                {form.round_trip && (
                  <div className="bg-card rounded-2xl p-5 space-y-3" data-testid="wizard-return-mode-card">
                    <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#B5838D]">Terugreis — overnachtingen</p>
                    <div className="flex flex-wrap gap-2">
                      <button data-testid="wizard-return-mode-auto" type="button" onClick={() => set({ return_stay_mode: "auto" })}
                        className={`px-4 py-2 rounded-full text-sm font-semibold transition-[background-color,color] duration-200 ${form.return_stay_mode === "auto" ? "bg-[#B5838D] text-white" : "bg-secondary hover:bg-[#B5838D]/15"}`}>
                        AI plant automatisch
                      </button>
                      <button data-testid="wizard-return-mode-custom" type="button" onClick={() => set({ return_stay_mode: "custom", planned_stays_return: form.planned_stays_return.length ? form.planned_stays_return : [{ location: "", name: "", nights: 1 }] })}
                        className={`px-4 py-2 rounded-full text-sm font-semibold transition-[background-color,color] duration-200 ${form.return_stay_mode === "custom" ? "bg-[#B5838D] text-white" : "bg-secondary hover:bg-[#B5838D]/15"}`}>
                        Eigen overnachtingen
                      </button>
                    </div>
                  </div>
                )}

                {form.round_trip && form.return_stay_mode === "custom" && (
                  <>
                    {savedStays.length > 0 && (
                      <div className="bg-card rounded-2xl p-4" data-testid="saved-return-stays-box">
                        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#B5838D] mb-2">Eerder gebruikte overnachtingen</p>
                        <div className="flex flex-wrap gap-2">
                          {savedStays.map((s) => (
                            <button key={s.id} data-testid={`saved-return-stay-chip-${s.id}`} type="button" onClick={() => addSavedStay(s, "planned_stays_return")}
                              className="rounded-full bg-[#B5838D]/10 hover:bg-[#B5838D]/20 text-[#B5838D] text-xs font-semibold px-3.5 py-2 active:scale-95 transition-[transform,background-color] duration-200">
                              + {s.location}{s.name ? ` · ${s.name}` : ""}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {form.planned_stays_return.map((ps, i) => (
                      <div key={i} id={`wiz-psr-${i}`} className={`bg-card rounded-2xl p-5 space-y-3 transition-shadow duration-300${flash(`wiz-psr-${i}`)}`}>
                        <div className="flex items-center justify-between">
                          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#B5838D]">Terugreis — overnachting {i + 1}</p>
                          <div className="flex items-center gap-1">
                            <button data-testid={`wizard-return-stay-up-${i}`} type="button" onClick={() => moveStay("planned_stays_return", i, -1)} disabled={i === 0} title="Schuif omhoog"
                              className="p-1.5 rounded-full hover:bg-[#B5838D]/10 text-[#B5838D] disabled:opacity-25 disabled:cursor-default transition-[background-color] duration-200">
                              <ChevronUp className="w-4 h-4" />
                            </button>
                            <button data-testid={`wizard-return-stay-down-${i}`} type="button" onClick={() => moveStay("planned_stays_return", i, 1)} disabled={i === form.planned_stays_return.length - 1} title="Schuif omlaag"
                              className="p-1.5 rounded-full hover:bg-[#B5838D]/10 text-[#B5838D] disabled:opacity-25 disabled:cursor-default transition-[background-color] duration-200">
                              <ChevronDown className="w-4 h-4" />
                            </button>
                            <button data-testid={`wizard-remove-return-stay-${i}`} type="button" onClick={() => removeReturnStay(i)} className="p-1.5 rounded-full hover:bg-red-50 text-red-500 transition-[background-color] duration-200">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <Input data-testid={`wizard-return-stay-location-${i}`} value={ps.location} onChange={(e) => setReturnStay(i, { location: e.target.value })} placeholder="Plaats, land — bv. Lyon, Frankrijk" className="h-12 rounded-xl" />
                          <Input data-testid={`wizard-return-stay-name-${i}`} value={ps.name} onChange={(e) => setReturnStay(i, { name: e.target.value })} placeholder="Hotel/verblijf (optioneel)" className="h-12 rounded-xl" />
                        </div>
                        <div className="flex items-center gap-3">
                          <Label className="text-sm text-muted-foreground">Nachten:</Label>
                          <button data-testid={`wizard-return-stay-nights-minus-${i}`} type="button" onClick={() => setReturnStay(i, { nights: Math.max(1, (parseInt(ps.nights) || 1) - 1) })}
                            className="w-8 h-8 rounded-full bg-secondary hover:bg-primary/15 flex items-center justify-center active:scale-90 transition-[transform,background-color] duration-150"><Minus className="w-3.5 h-3.5" /></button>
                          <span data-testid={`wizard-return-stay-nights-${i}`} className="font-mono font-semibold w-5 text-center">{ps.nights}</span>
                          <button data-testid={`wizard-return-stay-nights-plus-${i}`} type="button" onClick={() => setReturnStay(i, { nights: (parseInt(ps.nights) || 1) + 1 })}
                            className="w-8 h-8 rounded-full bg-secondary hover:bg-primary/15 flex items-center justify-center active:scale-90 transition-[transform,background-color] duration-150"><Plus className="w-3.5 h-3.5" /></button>
                        </div>
                        <LocFeedback status={locStatus[`psr-${i}`]} />
                      </div>
                    ))}
                    <button data-testid="wizard-add-return-stay-button" type="button" onClick={() => set({ planned_stays_return: [...form.planned_stays_return, { location: "", name: "", nights: 1 }] })}
                      className="w-full rounded-2xl border-2 border-dashed border-[#B5838D]/40 hover:border-[#B5838D] text-[#B5838D] font-semibold text-sm py-3 flex items-center justify-center gap-2 transition-[border-color] duration-200">
                      <Plus className="w-4 h-4" /> Terugreis-overnachting toevoegen
                    </button>
                    <p className="text-sm text-muted-foreground">De terugrit wordt gepland via deze overnachtingen, in volgorde.</p>
                  </>
                )}
                <p className="text-sm text-muted-foreground">De locaties worden gecontroleerd wanneer je op Volgende klikt.</p>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <h1 className="font-heading font-extrabold text-3xl tracking-tight">Hoe wil je rijden?</h1>
                <div className="space-y-4">
                  {routePrefs.map((p) => (
                    <button key={p.id} data-testid={`wizard-route-pref-${p.id}`} type="button" onClick={() => set({ route_preference: p.id })}
                      className={`w-full text-left rounded-2xl p-6 flex items-start gap-4 border-2 transition-[border-color,background-color] duration-200 ${form.route_preference === p.id ? "border-primary bg-primary/5" : "border-transparent bg-card hover:bg-secondary/60"}`}>
                      <span className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${form.route_preference === p.id ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                        <p.icon className="w-5 h-5" />
                      </span>
                      <span>
                        <span className="font-heading font-bold block">{p.title}</span>
                        <span className="text-sm text-muted-foreground">{p.text}</span>
                      </span>
                    </button>
                  ))}
                </div>
                {form.travel_mode !== "moto" && (<>
                <div className="space-y-2">
                  <Label>Voorkeur snellaadnetwerken (optioneel)</Label>
                  <div className="flex flex-wrap gap-2">
                    {[["tesla", "Tesla Supercharger"], ["ionity", "Ionity"], ["fastned", "Fastned"], ["allego", "Allego"], ["shell", "Shell Recharge"], ["total", "TotalEnergies"], ["enbw", "EnBW"], ["electra", "Electra"], ["vattenfall", "Vattenfall InCharge"]].map(([id, label]) => {
                      const active = form.preferred_networks.includes(id);
                      const logo = NETWORKS.find((n) => n.key === (id === "total" ? "totalenergies" : id))?.logo;
                      return (
                        <button key={id} data-testid={`wizard-network-${id}`} type="button"
                          onClick={() => set({ preferred_networks: active ? form.preferred_networks.filter((n) => n !== id) : [...form.preferred_networks, id] })}
                          className={`flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-semibold border-2 transition-[border-color,background-color,color] duration-200 ${active ? "border-primary bg-primary text-primary-foreground" : "border-transparent bg-card hover:bg-secondary"}`}>
                          {logo && (
                            <span className="inline-flex items-center justify-center bg-white rounded-md px-1.5 py-0.5">
                              <img src={logo} alt={label} className="h-4 max-w-[52px] object-contain" />
                            </span>
                          )}
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground">We kiezen laadstops bij voorkeur van deze aanbieders wanneer ze op je route liggen.</p>
                </div>
                {allCards.length > 0 && (
                  <div className="space-y-2" data-testid="wizard-charge-cards-section">
                    <Label>Mijn laadpassen (optioneel)</Label>
                    <div className="flex flex-wrap gap-2">
                      {allCards.map((c) => {
                        const active = form.charge_cards.includes(c.card_id);
                        return (
                          <button key={c.card_id} data-testid={`wizard-card-${c.card_id}`} type="button" onClick={() => toggleChargeCard(c.card_id)}
                            className={`px-3.5 py-2 rounded-full text-sm font-semibold border-2 transition-[border-color,background-color,color] duration-200 ${active ? "border-[#3A6EA5] bg-[#3A6EA5] text-white" : "border-transparent bg-card hover:bg-secondary"}`}>
                            {active ? "✓ " : "+ "}{c.name}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-muted-foreground">Passen met een eigen netwerk (IONITY, Fastned, Tesla, Shell, EnBW, Vattenfall) zetten dat netwerk automatisch als voorkeur hierboven — en je laadpas-advies in de Budget-tab wordt erop afgestemd.</p>
                  </div>
                )}
                </>)}
                {form.stay_mode === "auto" ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Max km per dag (optioneel)</Label>
                        <Input data-testid="wizard-max-km-input" type="number" min="50" value={form.max_km_per_day} onChange={(e) => set({ max_km_per_day: e.target.value })} placeholder={`standaard ${routePrefs.find((p) => p.id === form.route_preference)?.km}`} className="h-12 rounded-xl" />
                      </div>
                      <div className="space-y-2">
                        <Label>Max uur rijden per dag (optioneel)</Label>
                        <Input data-testid="wizard-max-hours-input" type="number" min="1" max="14" step="0.5" value={form.max_hours_per_day} onChange={(e) => set({ max_hours_per_day: e.target.value })} placeholder="bijv. 4" className="h-12 rounded-xl" />
                      </div>
                      <div className="space-y-2">
                        <Label>Overnachtingen onderweg naar bestemming (optioneel)</Label>
                        <Input data-testid="wizard-nights-input" type="number" min="0" value={form.nights_to_destination} onChange={(e) => set({ nights_to_destination: e.target.value })} placeholder="automatisch" className="h-12 rounded-xl" />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">Vul km, uren of allebei in — we verdelen de rit in dagen die binnen jouw limiet passen (de strengste limiet telt).</p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground bg-card rounded-2xl p-4">Je hebt je overnachtingen zelf opgegeven — de dagindeling volgt jouw verblijven.</p>
                )}
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6">
                <h1 className="font-heading font-extrabold text-3xl tracking-tight">Wie gaat er mee?</h1>
                <div className="space-y-4">
                  <Counter label="Volwassenen" value={form.travel_party.adults} min={1} testId="wizard-adults" onChange={(v) => set({ travel_party: { ...form.travel_party, adults: v } })} />
                  <Counter label="Kinderen" value={form.travel_party.children} testId="wizard-children" onChange={(v) => set({ travel_party: { ...form.travel_party, children: v } })} />
                  <Counter label="Huisdieren" value={form.travel_party.pets} testId="wizard-pets" onChange={(v) => set({ travel_party: { ...form.travel_party, pets: v } })} />
                </div>
                <div className="space-y-2">
                  <Label>Dagbudget in € (optioneel)</Label>
                  <Input data-testid="wizard-daily-budget-input" type="number" min="0" value={form.daily_budget} onChange={(e) => set({ daily_budget: e.target.value })} placeholder="bv. 200 per dag" className="h-12 rounded-xl" />
                  <p className="text-xs text-muted-foreground">We vergelijken de geraamde kosten (laden, verblijf, eten) met jouw budget.</p>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-6">
                <h1 className="font-heading font-extrabold text-3xl tracking-tight">Waar wil je overnachten?</h1>
                {form.stay_mode === "custom" ? (
                  <>
                    <div className="bg-card rounded-2xl p-5 flex items-center justify-between gap-4">
                      <div>
                        <p className="font-heading font-bold text-sm">AI-alternatieven naast jouw eigen hotels</p>
                        <p className="text-xs text-muted-foreground">Je eigen boekingen blijven leidend; AI toont per dag vrijblijvend één alternatief verblijf</p>
                      </div>
                      <Switch data-testid="wizard-suggest-alternatives-switch" checked={form.suggest_alternatives} onCheckedChange={(v) => set({ suggest_alternatives: v })} />
                    </div>
                    {form.suggest_alternatives ? (
                      <p className="text-muted-foreground text-sm">Kies de types waar de AI-alternatieven op moeten lijken.</p>
                    ) : (
                      <p data-testid="wizard-own-hotels-note" className="text-sm text-muted-foreground bg-card rounded-2xl p-4">Je hotels worden exact gebruikt zoals je ze hebt opgegeven — je hoeft hier verder niets te kiezen.</p>
                    )}
                  </>
                ) : (
                  <p className="text-muted-foreground">Kies één of meerdere types.</p>
                )}
                {(form.stay_mode !== "custom" || form.suggest_alternatives) && (
                <div className="grid grid-cols-2 gap-4">
                  {accoTypes.map((a) => {
                    const active = form.accommodation_types.includes(a.id);
                    return (
                      <button key={a.id} data-testid={`wizard-acco-${a.id}`} type="button" onClick={() => toggleAcco(a.id)}
                        className={`rounded-2xl p-6 flex flex-col items-center gap-3 border-2 transition-[border-color,background-color] duration-200 ${active ? "border-primary bg-primary/5" : "border-transparent bg-card hover:bg-secondary/60"}`}>
                        <span className={`w-12 h-12 rounded-full flex items-center justify-center ${active ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                          <a.icon className="w-6 h-6" />
                        </span>
                        <span className="font-heading font-bold">{a.label}</span>
                        {active && <Check className="w-4 h-4 text-primary" />}
                      </button>
                    );
                  })}
                </div>
                )}
              </div>
            )}

            {step === 5 && (
              <div className="space-y-6">
                <h1 className="font-heading font-extrabold text-3xl tracking-tight">Met welke {form.travel_mode === "moto" ? "moto" : "EV"} rijd je?</h1>
                {(() => {
                  const garageForMode = garage.filter((g) => (form.travel_mode === "moto") === ((g.vehicle || {}).type === "moto"));
                  if (!garageForMode.length) return null;
                  return (
                    <div className="space-y-2" data-testid="wizard-garage-section">
                      <p className="text-xs font-bold uppercase tracking-[0.12em] text-accent flex items-center gap-1.5">Uit je garage</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {garageForMode.map((g) => {
                          const v = g.vehicle || {};
                          const active = form.vehicle && form.vehicle.brand === v.brand && form.vehicle.model === v.model && (form.vehicle.rider_name || null) === (v.rider_name || null);
                          return (
                            <button key={g.id} data-testid={`wizard-garage-${g.id}`} type="button"
                              onClick={() => { setManual(false); set({ vehicle: v, rider_name: v.rider_name || "" }); }}
                              className={`text-left rounded-2xl p-4 border-2 transition-[border-color,background-color] duration-200 flex items-center gap-3 ${active ? "border-primary bg-primary/5" : "border-transparent bg-card hover:bg-secondary/60"}`}>
                              {g.has_photo
                                ? <img src={`${API_BASE}/garage/${g.id}/photo?v=${g._v || 0}`} alt={v.model} className="w-20 h-14 rounded-lg object-cover bg-secondary shrink-0" />
                                : <VehicleThumb brand={v.brand} model={v.model} vtype={v.type} />}
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5 mb-1">
                                  {active && <Check className="w-4 h-4 text-primary shrink-0" />}
                                  <span className="font-heading font-bold text-sm truncate">{g.nickname || `${v.brand} ${v.model}`}</span>
                                </div>
                                <p className="text-xs text-muted-foreground font-mono truncate">
                                  {g.nickname ? `${v.brand} ${v.model} · ` : ""}{v.rider_name ? `${v.rider_name} · ` : ""}±{v.range_km} km{v.type === "moto" ? " per tank" : ""}
                                </p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
                <div className="flex gap-2">
                  <button data-testid="wizard-vehicle-db-toggle" type="button" onClick={() => setManual(false)}
                    className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-[background-color,color] duration-200 ${!manual ? "bg-primary text-primary-foreground" : "bg-card hover:bg-secondary"}`}>
                    Uit database
                  </button>
                  <button data-testid="wizard-vehicle-manual-toggle" type="button" onClick={() => setManual(true)}
                    className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-[background-color,color] duration-200 ${manual ? "bg-primary text-primary-foreground" : "bg-card hover:bg-secondary"}`}>
                    Handmatig toevoegen
                  </button>
                </div>

                {!manual ? (
                  <>
                    <div className="flex gap-2 items-center">
                      <div className="relative flex-1">
                        <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input data-testid="wizard-vehicle-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Zoek in ${(form.travel_mode === "moto" ? motos : vehicles).length} modellen...`} className="h-12 rounded-xl pl-11" />
                      </div>
                      {(() => {
                        const favCount = (form.travel_mode === "moto" ? motos : vehicles).filter((v) => favIds.includes(v.id)).length;
                        return (
                          <button data-testid="wizard-fav-filter" type="button" onClick={() => setFavOnly(!favOnly)}
                            className={`h-12 px-4 rounded-xl text-sm font-semibold flex items-center gap-2 shrink-0 transition-colors ${favOnly ? "bg-amber-400/15 text-amber-600 border-2 border-amber-400/60" : "bg-card border-2 border-transparent hover:bg-secondary"}`}>
                            <Star className={`w-4 h-4 ${favOnly ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}`} />
                            Favorieten{favCount > 0 ? ` (${favCount})` : ""}
                          </button>
                        );
                      })()}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[420px] overflow-y-auto hide-scrollbar pr-1">
                      {filteredVehicles.slice(0, 60).map((v) => {
                        const active = form.vehicle?.id === v.id;
                        const fav = favIds.includes(v.id);
                        return (
                          <button key={v.id} data-testid={`wizard-vehicle-${v.id}`} type="button" onClick={() => set({ vehicle: v })}
                            className={`text-left rounded-2xl p-4 border-2 transition-[border-color,background-color] duration-200 flex items-center gap-3 ${active ? "border-primary bg-primary/5" : "border-transparent bg-card hover:bg-secondary/60"}`}>
                            <VehicleThumb brand={v.brand} model={v.model} vtype={v.type} />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5 mb-1">
                                {active && <Check className="w-4 h-4 text-primary shrink-0" />}
                                <span className="font-heading font-bold text-sm truncate">{v.brand} {v.model}</span>
                              </div>
                              <p className="text-xs text-muted-foreground font-mono">
                                {v.type === "moto" ? `±${v.range_km} km per tank · ${v.tank_liters} l · ${v.consumption_l_100km} l/100km` : `${v.range_km} km · ${v.battery_kwh} kWh · max ${v.max_charge_kw} kW`}
                              </p>
                            </div>
                            <span role="button" tabIndex={0} data-testid={`wizard-fav-${v.id}`}
                              title={fav ? "Verwijder uit favorieten" : "Voeg toe aan favorieten"}
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFav(v.id); }}
                              className="shrink-0 p-2 -mr-1 rounded-full hover:bg-secondary transition-colors">
                              <Star className={`w-4 h-4 ${fav ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {favOnly && filteredVehicles.length === 0 && (
                      <p className="text-sm text-muted-foreground" data-testid="wizard-fav-empty">Nog geen favorieten{search ? " gevonden met deze zoekopdracht" : ""} — klik op de ster bij een model om hem te bewaren.</p>
                    )}
                    {filteredVehicles.length > 60 && <p className="text-xs text-muted-foreground">{filteredVehicles.length} resultaten — verfijn je zoekopdracht om alles te zien.</p>}
                  </>
                ) : form.travel_mode === "moto" ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2 col-span-1"><Label>Merk</Label><Input data-testid="manual-brand-input" value={manualVehicle.brand} onChange={(e) => setManualVehicle({ ...manualVehicle, brand: e.target.value })} className="rounded-xl" /></div>
                    <div className="space-y-2 col-span-1"><Label>Model</Label><Input data-testid="manual-model-input" value={manualVehicle.model} onChange={(e) => setManualVehicle({ ...manualVehicle, model: e.target.value })} className="rounded-xl" /></div>
                    <div className="space-y-2"><Label>Bereik per tank (km)</Label><Input data-testid="manual-range-input" type="number" value={manualVehicle.range_km} onChange={(e) => setManualVehicle({ ...manualVehicle, range_km: e.target.value })} className="rounded-xl" /></div>
                    <div className="space-y-2"><Label>Tankinhoud (liter, optioneel)</Label><Input data-testid="manual-tank-input" type="number" value={manualVehicle.tank_liters} onChange={(e) => setManualVehicle({ ...manualVehicle, tank_liters: e.target.value })} className="rounded-xl" /></div>
                    <div className="space-y-2"><Label>Verbruik (l/100km, optioneel)</Label><Input data-testid="manual-fuel-consumption-input" type="number" step="0.1" value={manualVehicle.consumption_l_100km} onChange={(e) => setManualVehicle({ ...manualVehicle, consumption_l_100km: e.target.value })} className="rounded-xl" /></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2 col-span-1"><Label>Merk</Label><Input data-testid="manual-brand-input" value={manualVehicle.brand} onChange={(e) => setManualVehicle({ ...manualVehicle, brand: e.target.value })} className="rounded-xl" /></div>
                    <div className="space-y-2 col-span-1"><Label>Model</Label><Input data-testid="manual-model-input" value={manualVehicle.model} onChange={(e) => setManualVehicle({ ...manualVehicle, model: e.target.value })} className="rounded-xl" /></div>
                    <div className="space-y-2"><Label>Batterij (kWh)</Label><Input data-testid="manual-battery-input" type="number" value={manualVehicle.battery_kwh} onChange={(e) => setManualVehicle({ ...manualVehicle, battery_kwh: e.target.value })} className="rounded-xl" /></div>
                    <div className="space-y-2"><Label>Bereik WLTP (km)</Label><Input data-testid="manual-range-input" type="number" value={manualVehicle.range_km} onChange={(e) => setManualVehicle({ ...manualVehicle, range_km: e.target.value })} className="rounded-xl" /></div>
                    <div className="space-y-2"><Label>Verbruik (kWh/100km)</Label><Input data-testid="manual-consumption-input" type="number" value={manualVehicle.consumption_kwh_100km} onChange={(e) => setManualVehicle({ ...manualVehicle, consumption_kwh_100km: e.target.value })} className="rounded-xl" /></div>
                    <div className="space-y-2"><Label>Max laadsnelheid (kW)</Label><Input data-testid="manual-charge-input" type="number" value={manualVehicle.max_charge_kw} onChange={(e) => setManualVehicle({ ...manualVehicle, max_charge_kw: e.target.value })} className="rounded-xl" /></div>
                  </div>
                )}

                {form.travel_mode === "moto" && (
                  <div className="bg-card rounded-2xl p-5 space-y-4" data-testid="wizard-extra-motos">
                    <div>
                      <h3 className="font-heading font-bold">Rijders & extra moto's</h3>
                      <p className="text-xs text-muted-foreground mt-1">Rijden jullie met meerdere moto's? Voeg per volwassene een moto toe. Brandstof wordt voor alle moto's gerekend en we plannen met de kleinste actieradius van de groep.</p>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                      <span className="text-sm font-semibold w-20 shrink-0">Rijder 1</span>
                      <Input data-testid="wizard-rider-name-0" value={form.rider_name} onChange={(e) => set({ rider_name: e.target.value })} placeholder="Naam (optioneel)" className="h-11 rounded-xl sm:max-w-[200px]" />
                      <span className="text-xs text-muted-foreground truncate">{form.vehicle ? `${form.vehicle.brand} ${form.vehicle.model}` : manual ? `${manualVehicle.brand} ${manualVehicle.model}`.trim() || "kies hierboven" : "kies hierboven"}</span>
                    </div>
                    {form.extra_motos.map((em, i) => (
                      <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3" data-testid={`wizard-extra-moto-${i}`}>
                        <span className="text-sm font-semibold w-20 shrink-0">Rijder {i + 2}</span>
                        <Input data-testid={`wizard-rider-name-${i + 1}`} value={em.rider_name}
                          onChange={(e) => set({ extra_motos: form.extra_motos.map((x, j) => (j === i ? { ...x, rider_name: e.target.value } : x)) })}
                          placeholder="Naam (optioneel)" className="h-11 rounded-xl sm:max-w-[200px]" />
                        <MotoPicker motos={motos} garage={garage.filter((g) => (g.vehicle || {}).type === "moto")} value={em.vehicle} testId={`wizard-extra-moto-picker-${i}`}
                          onSelect={(m) => set({ extra_motos: form.extra_motos.map((x, j) => (j === i ? { ...x, vehicle: m, rider_name: x.rider_name || m.rider_name || "" } : x)) })} />
                        <button type="button" data-testid={`wizard-extra-moto-remove-${i}`} title="Verwijder deze rijder"
                          onClick={() => set({ extra_motos: form.extra_motos.filter((_, j) => j !== i) })}
                          className="self-start sm:self-auto p-2 rounded-full hover:bg-secondary text-muted-foreground transition-colors">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    {form.extra_motos.length < Math.max(form.travel_party.adults - 1, 0) ? (
                      <button type="button" data-testid="wizard-add-extra-moto"
                        onClick={() => set({ extra_motos: [...form.extra_motos, { rider_name: "", vehicle: null }] })}
                        className="flex items-center gap-2 rounded-full bg-secondary hover:bg-secondary/70 px-4 py-2.5 text-sm font-semibold transition-colors">
                        <Plus className="w-4 h-4" /> Moto toevoegen (rijder {form.extra_motos.length + 2})
                      </button>
                    ) : form.travel_party.adults <= 1 ? (
                      <p className="text-xs text-muted-foreground" data-testid="wizard-extra-moto-hint">Meer rijders? Zet bij stap "Gezelschap" het aantal volwassenen omhoog.</p>
                    ) : (
                      <p className="text-xs text-muted-foreground" data-testid="wizard-extra-moto-max">Maximum bereikt: één moto per volwassene ({form.travel_party.adults}).</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {step === 6 && (
              <div className="space-y-6">
                <h1 className="font-heading font-extrabold text-3xl tracking-tight">{editId ? "Klaar om te herberekenen?" : "Klaar om te genereren?"}</h1>
                {editId && (
                  <div data-testid="wizard-edit-banner" className="rounded-2xl p-4 bg-[#3A6EA5]/10 border border-[#3A6EA5]/25 text-sm">
                    Je past een bestaande trip aan. <strong>Reisverslagen, foto's, reacties en gedeelde links blijven behouden.</strong> Al gereden dagen blijven staan — alleen de rest van je trip wordt herberekend (tenzij je hieronder anders kiest).
                  </div>
                )}
                {editId && (
                  <label data-testid="wizard-full-replan" className="flex items-start gap-3 rounded-2xl p-4 border border-border bg-card text-sm cursor-pointer">
                    <input type="checkbox" checked={fullReplan} onChange={(e) => setFullReplan(e.target.checked)}
                      className="mt-0.5 accent-primary w-4 h-4" data-testid="wizard-full-replan-checkbox" />
                    <span>
                      <strong>Ook al gereden dagen opnieuw plannen</strong>{" "}
                      <span className="text-muted-foreground">— bouwt de volledige trip opnieuw op, inclusief het verleden. Gebruik dit alleen om fouten in eerdere dagen te herstellen; aankomst-vinkjes en werkelijke kosten van die dagen gaan hierbij verloren.</span>
                    </span>
                  </label>
                )}
                <div className="bg-card rounded-2xl p-6 space-y-3 text-sm">
                  <p><span className="text-muted-foreground">Trip:</span> <span className="font-semibold">{form.name}</span></p>
                  <p><span className="text-muted-foreground">Data:</span> {form.start_date} t/m {form.end_date}</p>
                  <p><span className="text-muted-foreground">Route:</span> {buildQuery(form.origin_street, form.origin_city)} → {buildQuery(form.destination_street, form.destination_city)}{form.round_trip ? ` → ${form.return_end.trim() || "terug"}` : ""}</p>
                  {form.waypoints.filter((w) => w.trim()).length > 0 && <p data-testid="wizard-summary-via"><span className="text-muted-foreground">Via:</span> {form.waypoints.filter((w) => w.trim()).join(" · ")}{form.stay_mode === "custom" ? " · zonder overnachting" : ""}</p>}
                  {(form.avoid_places.filter((a) => a.trim()).length > 0 || form.avoid_toll || form.avoid_ferry) && (
                    <p data-testid="wizard-summary-avoid"><span className="text-muted-foreground">Vermijdt:</span> {[...form.avoid_places.filter((a) => a.trim()), ...(form.avoid_toll ? ["tolwegen"] : []), ...(form.avoid_ferry ? ["veerboten"] : [])].join(" · ")}</p>
                  )}
                  {form.stay_mode === "custom" && <p><span className="text-muted-foreground">Overnachtingen:</span> {form.planned_stays.filter((p) => p.location.trim()).map((p) => `${p.location}${p.name ? ` (${p.name})` : ""} — ${p.nights}n`).join(" · ")}</p>}
                  <p><span className="text-muted-foreground">Type:</span> {form.round_trip ? "Heen en terug" : form.destination_is_home ? "Enkele reis — eindigt thuis (geen overnachting op eindpunt)" : "Enkele reis"}</p>
                  <p><span className="text-muted-foreground">Voorkeur:</span> {routePrefs.find((p) => p.id === form.route_preference)?.title}{form.stay_mode === "auto" && form.max_km_per_day && <> · max <span className="font-mono">{form.max_km_per_day} km</span>/dag</>}{form.stay_mode === "auto" && form.max_hours_per_day && <> · max <span className="font-mono">{form.max_hours_per_day} u</span> rijden/dag</>}{form.stay_mode === "auto" && form.nights_to_destination && <> · {form.nights_to_destination} overnacht. onderweg</>}</p>
                  <p><span className="text-muted-foreground">Gezelschap:</span> {form.travel_party.adults} volw. · {form.travel_party.children} kind. · {form.travel_party.pets} huisd.</p>
                  <p><span className="text-muted-foreground">Verblijf:</span> {form.accommodation_types.map((a) => accoTypes.find((t) => t.id === a)?.label || a).join(", ")}</p>
                  <p><span className="text-muted-foreground">Reismodus:</span> {form.travel_mode === "moto" ? "Moto 🏍️" : "Elektrische auto ⚡"}</p>
                  <p><span className="text-muted-foreground">Voertuig:</span> {form.vehicle?.brand} {form.vehicle?.model} <span className="font-mono text-xs">({form.vehicle?.range_km} km{form.travel_mode === "moto" ? " per tank" : ""})</span>{form.travel_mode === "moto" && form.rider_name.trim() ? <span className="text-muted-foreground"> — {form.rider_name.trim()}</span> : null}</p>
                  {form.travel_mode === "moto" && form.extra_motos.filter((em) => em.vehicle).length > 0 && (
                    <p data-testid="wizard-summary-extra-motos"><span className="text-muted-foreground">Extra moto's:</span> {form.extra_motos.filter((em) => em.vehicle).map((em, i) => `${em.rider_name.trim() ? `${em.rider_name.trim()} — ` : ""}${em.vehicle.brand} ${em.vehicle.model}`).join(" · ")}</p>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{form.travel_mode === "moto"
                  ? "We berekenen je route, plannen tankstops waar nodig en genereren verblijf-, activiteiten- en paklijst-suggesties met AI. Dit duurt 1 à 2 minuten."
                  : "We berekenen je route, plannen laadstops op basis van je batterij en genereren verblijf-, activiteiten- en paklijst-suggesties met AI. Dit duurt 1 à 2 minuten."}</p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex justify-between mt-12">
          <Button data-testid="wizard-prev-button" variant="ghost" disabled={step === 0} onClick={() => setStep((s) => s - 1)} className="rounded-full">
            <ArrowLeft className="w-4 h-4 mr-1" /> Vorige
          </Button>
          {step < STEPS.length - 1 ? (
            <Button data-testid="wizard-next-button" disabled={checking} onClick={next}
              className={`rounded-full bg-primary hover:bg-[#1B4332] px-8 active:scale-95 transition-[transform,background-color] duration-200 ${canNext() ? "" : "opacity-60"}`}>
              {step === 1 && checking ? "Locaties controleren..." : <>Volgende <ArrowRight className="w-4 h-4 ml-1" /></>}
            </Button>
          ) : (
            <Button data-testid="wizard-generate-button" disabled={submitting} onClick={submit}
              className="rounded-full bg-primary hover:bg-[#1B4332] px-8 active:scale-95 transition-[transform,background-color] duration-200">
              <Zap className="w-4 h-4 mr-2" /> {submitting ? "Bezig..." : editId ? "Herbereken mijn trip" : "Genereer mijn trip"}
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
